import { useState } from 'react'
import { router } from '@inertiajs/react'
import { Link } from '@inertiajs/react'
import { Plus, LogIn, Search } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import AdminLayout from '@/layouts/AdminLayout'
import { formatDate } from '@/lib/formatters'

interface Organization {
  id: string
  name: string
  slug: string
  user_count: number
  status: 'active' | 'suspended'
  created_at: string
}

interface Props {
  organizations: Organization[]
}

export default function OrganizationsIndex({ organizations }: Props) {
  const [search, setSearch] = useState('')

  const filtered = organizations.filter((org) => {
    if (!search) return true
    const q = search.toLowerCase()
    return org.name.toLowerCase().includes(q) || org.slug.toLowerCase().includes(q)
  })

  return (
    <>
      <header className="flex h-14 items-center justify-between border-b px-8">
        <h1 className="text-sm font-semibold">Organizations</h1>
        <Button
          size="sm"
          className="text-xs"
          onClick={() => router.visit('/admin/organizations/new')}
        >
          <Plus className="mr-1 h-3.5 w-3.5" />
          New Organization
        </Button>
      </header>

      {/* Toolbar */}
      <div className="flex items-center gap-4 border-b px-8 py-3">
        <div className="relative w-64">
          <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search organizations..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-8 pl-8 text-xs"
          />
        </div>
        <span className="text-xs text-muted-foreground">
          {filtered.length} {filtered.length === 1 ? 'organization' : 'organizations'}
        </span>
      </div>

      <div className="flex-1 overflow-y-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b bg-muted/30 text-left">
              <th className="px-6 py-2.5 text-xs font-medium text-muted-foreground">Organization</th>
              <th className="px-6 py-2.5 text-xs font-medium text-muted-foreground">Slug</th>
              <th className="px-6 py-2.5 text-xs font-medium text-muted-foreground">Users</th>
              <th className="px-6 py-2.5 text-xs font-medium text-muted-foreground">Created</th>
              <th className="px-6 py-2.5 text-xs font-medium text-muted-foreground">Status</th>
              <th className="px-6 py-2.5 text-xs font-medium text-muted-foreground"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((org) => (
              <tr key={org.id} className="border-b hover:bg-muted/30">
                <td className="px-6 py-3 text-[13px] font-medium">
                  <Link
                    href={`/admin/organizations/${org.id}`}
                    className="text-foreground hover:text-blue-600 hover:underline cursor-pointer"
                  >
                    {org.name}
                  </Link>
                </td>
                <td className="px-6 py-3 text-[13px] text-muted-foreground font-mono">{org.slug}</td>
                <td className="px-6 py-3 text-[13px] text-foreground">{org.user_count}</td>
                <td className="px-6 py-3 text-[13px] text-muted-foreground">{formatDate(org.created_at)}</td>
                <td className="px-6 py-3">
                  {org.status === 'active' ? (
                    <Badge
                      variant="secondary"
                      size="sm"
                      className="bg-emerald-50 text-emerald-600"
                    >
                      Active
                    </Badge>
                  ) : (
                    <Badge
                      variant="secondary"
                      size="sm"
                      className="bg-red-100 text-red-800"
                    >
                      Suspended
                    </Badge>
                  )}
                </td>
                <td className="px-6 py-3 text-right">
                  <Button
                    variant="ghost"
                    size="sm"
                    className={`h-7 text-xs cursor-pointer ${
                      org.status === 'suspended'
                        ? 'text-muted-foreground opacity-50 cursor-not-allowed'
                        : 'text-blue-600 hover:text-blue-700'
                    }`}
                    disabled={org.status === 'suspended'}
                    onClick={() => {
                      router.post(`/admin/organizations/${org.id}/impersonate`)
                    }}
                  >
                    <LogIn className="mr-1 h-3.5 w-3.5" />
                    Impersonate
                  </Button>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={6} className="px-6 py-8 text-center text-sm text-muted-foreground">
                  No organizations found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </>
  )
}

OrganizationsIndex.layout = (page: React.ReactNode) => (
  <AdminLayout>{page}</AdminLayout>
)
